<template>
  <div class="modal fade" id="disputeDetailModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-file-document-outline me-2"></i>
            Dispute Details - {{ disputeDetail.disputeId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Dispute Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-light">
                  <h5 class="mb-0">Dispute Summary</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td><strong>Dispute ID:</strong></td>
                          <td>{{ disputeDetail.disputeId }}</td>
                        </tr>
                        <tr>
                          <td><strong>Reference No:</strong></td>
                          <td>{{ disputeDetail.referenceNo }}</td>
                        </tr>
                        <tr>
                          <td><strong>Customer:</strong></td>
                          <td>{{ disputeDetail.customerName }}</td>
                        </tr>
                        <tr>
                          <td><strong>Phone:</strong></td>
                          <td>{{ disputeDetail.customerPhone }}</td>
                        </tr>
                        <tr>
                          <td><strong>Type:</strong></td>
                          <td>
                            <span class="badge bg-info">{{ formatDisputeType(disputeDetail.disputeType) }}</span>
                          </td>
                        </tr>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>
                            <span class="badge" :class="getStatusClass(disputeDetail.status)">
                              {{ formatStatus(disputeDetail.status) }}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Priority:</strong></td>
                          <td>
                            <span class="badge" :class="getPriorityClass(disputeDetail.priority)">
                              {{ disputeDetail.priority }}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Amount:</strong></td>
                          <td class="fw-bold">{{ formatPrice(disputeDetail.disputeAmount) }}</td>
                        </tr>
                        <tr>
                          <td><strong>Date Raised:</strong></td>
                          <td>{{ disputeDetail.dateRaised }}</td>
                        </tr>
                        <tr>
                          <td><strong>Assigned To:</strong></td>
                          <td>{{ disputeDetail.assignedTo || 'Unassigned' }}</td>
                        </tr>
                      </table>
                    </div>
                  </div>

                  <div class="row mt-3">
                    <div class="col-12">
                      <h6>Description:</h6>
                      <p class="text-muted">{{ disputeDetail.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabs for different sections -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#timeline" role="tab">
                <i class="mdi mdi-timeline me-1"></i>
                Timeline
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#comments" role="tab">
                <i class="mdi mdi-comment-multiple me-1"></i>
                Comments ({{ mockComments.length }})
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#documents" role="tab">
                <i class="mdi mdi-file-multiple me-1"></i>
                Documents ({{ mockDocuments.length }})
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#related-records" role="tab">
                <i class="mdi mdi-link me-1"></i>
                Related Records
              </a>
            </li>
          </ul>

          <div class="tab-content mt-3">
            <!-- Timeline Tab -->
            <div class="tab-pane fade show active" id="timeline" role="tabpanel">
              <div class="timeline">
                <div v-for="(event, index) in mockTimeline" :key="index" class="timeline-item">
                  <div class="timeline-marker" :class="getTimelineMarkerClass(event.type)">
                    <i :class="getTimelineIcon(event.type)"></i>
                  </div>
                  <div class="timeline-content">
                    <div class="d-flex justify-content-between">
                      <h6 class="mb-1">{{ event.title }}</h6>
                      <small class="text-muted">{{ event.date }}</small>
                    </div>
                    <p class="text-muted mb-1">{{ event.description }}</p>
                    <small class="text-muted">by {{ event.user }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Tab -->
            <div class="tab-pane fade" id="comments" role="tabpanel">
              <!-- Add Comment Form -->
              <div class="card mb-3">
                <div class="card-body">
                  <h6 class="mb-3">Add Comment</h6>
                  <div class="mb-3">
                    <textarea v-model="newComment" class="form-control" rows="3"
                              placeholder="Type your comment here..."></textarea>
                  </div>
                  <div class="d-flex justify-content-between">
                    <div class="form-check">
                      <input v-model="isInternalComment" class="form-check-input" type="checkbox" id="internalComment">
                      <label class="form-check-label" for="internalComment">
                        Internal Comment (Not visible to customer)
                      </label>
                    </div>
                    <button @click="addComment" :disabled="!newComment.trim()" class="btn btn-primary btn-sm">
                      <i class="mdi mdi-send me-1"></i>
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>

              <!-- Comments List -->
              <div class="comments-list">
                <div v-for="(comment, index) in mockComments" :key="index" class="card mb-2">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center mb-2">
                          <strong class="me-2">{{ comment.user }}</strong>
                          <span v-if="comment.isInternal" class="badge bg-warning text-dark">Internal</span>
                        </div>
                        <p class="mb-2">{{ comment.message }}</p>
                        <small class="text-muted">{{ comment.date }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents Tab -->
            <div class="tab-pane fade" id="documents" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead class="table-light">
                      <tr>
                        <th>Document Name</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Uploaded By</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(doc, index) in mockDocuments" :key="index">
                        <td>
                          <i :class="getDocumentIcon(doc.type)" class="me-2"></i>
                          {{ doc.name }}
                        </td>
                        <td>{{ doc.type }}</td>
                        <td>{{ doc.size }}</td>
                        <td>{{ doc.uploadedBy }}</td>
                        <td>{{ doc.date }}</td>
                        <td>
                          <button @click="downloadDocument(doc)" class="btn btn-outline-primary btn-sm me-1">
                            <i class="mdi mdi-download"></i>
                          </button>
                          <button @click="viewDocument(doc)" class="btn btn-outline-info btn-sm">
                            <i class="mdi mdi-eye"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Related Records Tab -->
            <div class="tab-pane fade" id="related-records" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="mb-0">Related Bills</h6>
                    </div>
                    <div class="card-body">
                      <div v-for="(bill, index) in mockRelatedBills" :key="index" class="border-bottom pb-2 mb-2">
                        <div class="d-flex justify-content-between">
                          <div>
                            <strong>{{ bill.billNo }}</strong>
                            <p class="text-muted mb-0">{{ bill.description }}</p>
                          </div>
                          <div class="text-end">
                            <div class="fw-bold">{{ formatPrice(bill.amount) }}</div>
                            <span class="badge" :class="bill.status === 'PAID' ? 'bg-success' : 'bg-warning'">
                              {{ bill.status }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="mb-0">Payment History</h6>
                    </div>
                    <div class="card-body">
                      <div v-for="(payment, index) in mockPaymentHistory" :key="index" class="border-bottom pb-2 mb-2">
                        <div class="d-flex justify-content-between">
                          <div>
                            <strong>{{ payment.receiptNo }}</strong>
                            <p class="text-muted mb-0">{{ payment.method }}</p>
                            <small class="text-muted">{{ payment.date }}</small>
                          </div>
                          <div class="text-end">
                            <div class="fw-bold text-success">{{ formatPrice(payment.amount) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button v-if="disputeDetail.status !== 'RESOLVED' && disputeDetail.status !== 'REJECTED'"
                      @click="updateStatus('UNDER_REVIEW')" class="btn btn-info me-2">
                <i class="mdi mdi-eye me-1"></i>
                Mark Under Review
              </button>
              <button v-if="disputeDetail.status !== 'RESOLVED' && disputeDetail.status !== 'REJECTED'"
                      @click="updateStatus('ESCALATED')" class="btn btn-warning me-2">
                <i class="mdi mdi-arrow-up-bold me-1"></i>
                Escalate
              </button>
            </div>
            <div>
              <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
                Close
              </button>
              <button v-if="disputeDetail.status !== 'RESOLVED' && disputeDetail.status !== 'REJECTED'"
                      @click="resolveDispute" class="btn btn-success">
                <i class="mdi mdi-check me-1"></i>
                Resolve Dispute
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "DisputeDetailModal",
  props: {
    disputeDetail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      newComment: '',
      isInternalComment: false,

      // Mock data for demonstration
      mockTimeline: [
        {
          type: 'created',
          title: 'Dispute Raised',
          description: 'Customer raised a dispute regarding billing error',
          user: 'John Doe',
          date: '2025-01-15 10:30:00'
        },
        {
          type: 'assigned',
          title: 'Dispute Assigned',
          description: 'Dispute assigned to Alice Wanjiku for investigation',
          user: 'System Admin',
          date: '2025-01-15 11:15:00'
        },
        {
          type: 'comment',
          title: 'Comment Added',
          description: 'Initial investigation started, reviewing billing records',
          user: 'Alice Wanjiku',
          date: '2025-01-15 14:30:00'
        },
        {
          type: 'status_change',
          title: 'Status Changed',
          description: 'Status changed from PENDING to UNDER_REVIEW',
          user: 'Alice Wanjiku',
          date: '2025-01-15 15:00:00'
        }
      ],

      mockComments: [
        {
          id: 1,
          user: 'Alice Wanjiku',
          message: 'Started investigating the billing error. Reviewing the customer\'s business category and applicable rates.',
          date: '2025-01-15 14:30:00',
          isInternal: true
        },
        {
          id: 2,
          user: 'John Doe',
          message: 'I have attached my business registration certificate which shows I should be in category C, not category A.',
          date: '2025-01-15 16:45:00',
          isInternal: false
        },
        {
          id: 3,
          user: 'Alice Wanjiku',
          message: 'Thank you for the documentation. I can confirm there was an error in categorization. Processing the correction now.',
          date: '2025-01-16 09:15:00',
          isInternal: false
        }
      ],

      mockDocuments: [
        {
          id: 1,
          name: 'Business_Registration_Certificate.pdf',
          type: 'PDF',
          size: '245 KB',
          uploadedBy: 'John Doe',
          date: '2025-01-15 16:45:00'
        },
        {
          id: 2,
          name: 'Original_Bill_SBP250001.pdf',
          type: 'PDF',
          size: '156 KB',
          uploadedBy: 'System',
          date: '2025-01-15 10:30:00'
        },
        {
          id: 3,
          name: 'Bank_Statement.pdf',
          type: 'PDF',
          size: '890 KB',
          uploadedBy: 'John Doe',
          date: '2025-01-15 16:50:00'
        }
      ],

      mockRelatedBills: [
        {
          billNo: 'SBP250001',
          description: 'Single Business Permit - 2025',
          amount: 15000,
          status: 'UNPAID'
        },
        {
          billNo: 'SBP240890',
          description: 'Single Business Permit - 2024',
          amount: 12000,
          status: 'PAID'
        },
        {
          billNo: 'LR456789',
          description: 'Land Rates - Q4 2024',
          amount: 8500,
          status: 'PAID'
        }
      ],

      mockPaymentHistory: [
        {
          receiptNo: 'RCT456123',
          method: 'M-Pesa',
          amount: 12000,
          date: '2024-12-15 14:30:00'
        },
        {
          receiptNo: 'RCT456124',
          method: 'Bank Transfer',
          amount: 8500,
          date: '2024-11-20 10:15:00'
        }
      ]
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDisputeType(type) {
      const types = {
        'BILLING_ERROR': 'Billing Error',
        'PAYMENT_NOT_REFLECTED': 'Payment Not Reflected',
        'OVERCHARGE': 'Overcharge',
        'DUPLICATE_CHARGE': 'Duplicate Charge',
        'REFUND_REQUEST': 'Refund Request',
        'OTHER': 'Other'
      };
      return types[type] || type;
    },

    formatStatus(status) {
      const statuses = {
        'PENDING': 'Pending',
        'UNDER_REVIEW': 'Under Review',
        'RESOLVED': 'Resolved',
        'REJECTED': 'Rejected',
        'ESCALATED': 'Escalated'
      };
      return statuses[status] || status;
    },

    getStatusClass(status) {
      switch (status) {
        case 'PENDING':
          return 'bg-warning';
        case 'UNDER_REVIEW':
          return 'bg-info';
        case 'RESOLVED':
          return 'bg-success';
        case 'REJECTED':
          return 'bg-danger';
        case 'ESCALATED':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },

    getPriorityClass(priority) {
      switch (priority) {
        case 'LOW':
          return 'bg-light text-dark';
        case 'MEDIUM':
          return 'bg-info';
        case 'HIGH':
          return 'bg-warning';
        case 'URGENT':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getTimelineMarkerClass(type) {
      switch (type) {
        case 'created':
          return 'bg-primary';
        case 'assigned':
          return 'bg-info';
        case 'comment':
          return 'bg-warning';
        case 'status_change':
          return 'bg-success';
        case 'resolved':
          return 'bg-success';
        case 'rejected':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getTimelineIcon(type) {
      switch (type) {
        case 'created':
          return 'mdi mdi-plus';
        case 'assigned':
          return 'mdi mdi-account-plus';
        case 'comment':
          return 'mdi mdi-comment';
        case 'status_change':
          return 'mdi mdi-swap-horizontal';
        case 'resolved':
          return 'mdi mdi-check';
        case 'rejected':
          return 'mdi mdi-close';
        default:
          return 'mdi mdi-circle';
      }
    },

    getDocumentIcon(type) {
      switch (type.toLowerCase()) {
        case 'pdf':
          return 'mdi mdi-file-pdf-box text-danger';
        case 'doc':
        case 'docx':
          return 'mdi mdi-file-word-box text-primary';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'mdi mdi-file-image-box text-info';
        default:
          return 'mdi mdi-file-document-box text-secondary';
      }
    },

    addComment() {
      if (!this.newComment.trim()) return;

      const comment = {
        id: this.mockComments.length + 1,
        user: 'Current User', // In real app, get from session
        message: this.newComment,
        date: moment().format('YYYY-MM-DD HH:mm:ss'),
        isInternal: this.isInternalComment
      };

      this.mockComments.push(comment);

      // Add to timeline
      this.mockTimeline.push({
        type: 'comment',
        title: 'Comment Added',
        description: this.isInternalComment ? 'Internal comment added' : 'Public comment added',
        user: 'Current User',
        date: moment().format('YYYY-MM-DD HH:mm:ss')
      });

      this.$emit('comment-added', this.disputeDetail.disputeId, comment);

      // Reset form
      this.newComment = '';
      this.isInternalComment = false;
    },

    updateStatus(newStatus) {
      this.$emit('status-updated', this.disputeDetail.disputeId, newStatus);

      // Add to timeline
      this.mockTimeline.push({
        type: 'status_change',
        title: 'Status Changed',
        description: `Status changed to ${this.formatStatus(newStatus)}`,
        user: 'Current User',
        date: moment().format('YYYY-MM-DD HH:mm:ss')
      });
    },

    resolveDispute() {
      // In real implementation, this might open a resolution modal
      // For now, just emit the event
      this.$emit('status-updated', this.disputeDetail.disputeId, 'RESOLVED');

      // Close modal
      const modalElement = document.getElementById('disputeDetailModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    downloadDocument(doc) {
      // Simulate document download
      console.log('Downloading document:', doc.name);
      alert(`Downloading ${doc.name}...`);
    },

    viewDocument(doc) {
      // Simulate document viewing
      console.log('Viewing document:', doc.name);
      alert(`Opening ${doc.name} for viewing...`);
    }
  }
}
</script>

<style scoped>
.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: -19px;
  top: 30px;
  width: 2px;
  height: calc(100% + 10px);
  background-color: #dee2e6;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  top: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.timeline-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #007bff;
}

.comments-list {
  max-height: 400px;
  overflow-y: auto;
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  border-bottom-color: #0d6efd;
  font-weight: 600;
}

.table-borderless td,
.table-borderless th {
  border: none;
}

.tab-content {
  min-height: 300px;
}

.modal-xl {
  max-width: 1200px;
}
</style>